import { useEffect, useState } from 'react';
import { updateUser, createUser, subscribeToUsers, assignSubjectsToTeacher, toggleUserStatus, deleteUserAccount } from '../../services/userService';
import { MdAdd, MdRemoveRedEye, MdSearch, MdEdit, MdDelete, MdToggleOff, MdToggleOn, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import { USER_ROLES } from '../../utils/constants';

const AVAILABLE_SUBJECTS = {
  'JSS1': [
    'english_jss1',
    'math_jss1',
    'phe_jss1',
    'crs_jss1',
    'irs_jss1',
    'nigerian_history_jss1',
    'social_citizenship_jss1',
    'cca_jss1',
    'french_jss1',
    'intermediate_science_jss1',
    'digital_technologies_jss1',
    'business_studies_jss1',
    'trade_subjects_jss1',
  ],
  'JSS2': [
    'english_jss2',
    'math_jss2',
    'phe_jss2',
    'crs_jss2',
    'irs_jss2',
    'nigerian_history_jss2',
    'social_citizenship_jss2',
    'cca_jss2',
    'french_jss2',
    'intermediate_science_jss2',
    'digital_technologies_jss2',
    'business_studies_jss2',
    'trade_subjects_jss2',
  ],
  'JSS3': [
    'english_jss3',
    'math_jss3',
    'phe_jss3',
    'crs_jss3',
    'irs_jss3',
    'nigerian_history_jss3',
    'social_citizenship_jss3',
    'cca_jss3',
    'french_jss3',
    'intermediate_science_jss3',
    'digital_technologies_jss3',
    'business_studies_jss3',
    'trade_subjects_jss3',
  ],
  'SS1': [
    'english_ss1', 'math_ss1', 'physics_ss1', 'chem_ss1', 'bio_ss1', 
    'fmaths_ss1', 'agric_ss1', 'economics_ss1', 'accounting_ss1', 
    'fin_accounting_ss1', 'marketing_ss1', 'literature_ss1', 'govt_ss1'
  ],
  'SS2': [
    'english_ss2', 'math_ss2', 'physics_ss2', 'chem_ss2', 'bio_ss2', 
    'fmaths_ss2', 'agric_ss2', 'economics_ss2', 'accounting_ss2', 
    'fin_accounting_ss2', 'marketing_ss2', 'literature_ss2', 'govt_ss2'
  ],
  'SS3': [
    'english_ss3', 'math_ss3', 'physics_ss3', 'chem_ss3', 'bio_ss3', 
    'fmaths_ss3', 'agric_ss3', 'economics_ss3', 'accounting_ss3', 
    'fin_accounting_ss3', 'marketing_ss3', 'literature_ss3', 'govt_ss3'
  ],
  'UTME': [
    'math_utme', 'english_utme', 'physics_utme', 'chem_utme', 'bio_utme', 
    'fmaths_utme', 'economics_utme', 'govt_utme', 'literature_utme',
    'agric_utme', 'fin_accounting_utme', 'marketing_utme', 'commerce_utme',
    'digital_tech_utme', 'geography_utme'
  ],
};

const SUBJECT_NAMES = {
  // Core & JSS Official Subjects
  'english': 'English Language',
  'english_studies': 'English Studies',
  'math': 'Mathematics',
  'mathematics': 'Mathematics',
  'phe': 'Physical and Health Education',
  'crs': 'Christian Religious Studies',
  'irs': 'Islamic Studies',
  'nigerian_history': 'Nigerian History',
  'social_citizenship': 'Social and Citizenship Studies',
  'cca': 'Cultural and Creative Arts (CCA)',
  'french': 'French',
  'intermediate_science': 'Intermediate Science',
  'digital_technologies': 'Digital Technologies',
  'business_studies': 'Business Studies',
  'trade_subjects': 'Trade Subjects',

  // Senior Subjects
  'physics': 'Physics',
  'chem': 'Chemistry',
  'bio': 'Biology',
  'fmaths': 'Further Maths',
  'agric': 'Agricultural Science',
  'economics': 'Economics',
  'accounting': 'Accounting',
  'fin_accounting': 'Financial Accounting',
  'marketing': 'Marketing',
  'literature': 'Literature in English',
  'govt': 'Government',
  'digital_tech': 'Digital Technology',
  'commerce': 'Commerce',
  'geography': 'Geography',

  // Legacy / Aliases
  'basic_science': 'Intermediate Science',
  'basic_tech': 'Basic Technology',
  'social_studies': 'Social and Citizenship Studies',
  'civic': 'Social and Citizenship Studies',
  'ict': 'Digital Technologies',
  'home_econ': 'Home Economics',
};

const formatSubject = (subjectId) => {
  if (!subjectId) return '';
  const parts = subjectId.split('_');
  if (parts.length < 2) return subjectId;
  const level = parts.pop().toUpperCase();
  const base = parts.join('_');
  let name = SUBJECT_NAMES[base] || base;
  if (base === 'english') {
    name = level.startsWith('JSS') ? 'English Studies' : 'English Language';
  }
  return `${name} (${level})`;
};

const formatSubjectShort = (subjectId) => {
  if (!subjectId) return '';
  const parts = subjectId.split('_');
  if (parts.length < 2) return SUBJECT_NAMES[subjectId] || subjectId;
  const level = parts[parts.length - 1].toLowerCase();
  const base = parts.slice(0, -1).join('_');
  if (base === 'english') {
    return level.startsWith('jss') ? 'English Studies' : 'English Language';
  }
  return SUBJECT_NAMES[base] || SUBJECT_NAMES[subjectId] || base;
};

const Teachers = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTeacher, setNewTeacher] = useState({
    fullName: '',
    email: '',
    password: '',
    isActive: true,
    assignedSubjects: [],
  });
  const [showNewTeacherPassword, setShowNewTeacherPassword] = useState(false);
  const [isAddingTeacher, setIsAddingTeacher] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [teacherToView, setTeacherToView] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isEditingSubjects, setIsEditingSubjects] = useState(false);
  const [editedSubjects, setEditedSubjects] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingSubjects, setIsSavingSubjects] = useState(false);

  // Subscribe to teachers data
  useEffect(() => {
    const unsubscribe = subscribeToUsers((usersData) => {
      const teachersOnly = usersData.filter(user => user.role === USER_ROLES.TEACHER);
      setTeachers(teachersOnly);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get unique subjects from all teachers
  const allSubjects = [...new Set(teachers.flatMap(t => t.assignedSubjects || []))];

  // Filter teachers by subject and search
  useEffect(() => {
    let filtered = teachers;

    if (subjectFilter !== 'all') {
      filtered = filtered.filter(teacher => 
        teacher.assignedSubjects?.includes(subjectFilter)
      );
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        teacher =>
          teacher.fullName?.toLowerCase().includes(lowerSearch) ||
          teacher.email?.toLowerCase().includes(lowerSearch)
      );
    }

    setFilteredTeachers(filtered);
  }, [teachers, subjectFilter, searchTerm]);

  const handleViewTeacher = (teacher) => {
    setTeacherToView(teacher);
    setEditedSubjects(teacher.assignedSubjects || []);
    setIsEditingSubjects(false);
    setShowViewModal(true);
  };

  const toggleNewTeacherSubject = (subject) => {
    const current = newTeacher.assignedSubjects || [];
    if (current.includes(subject)) {
      setNewTeacher(prev => ({
        ...prev,
        assignedSubjects: current.filter(s => s !== subject),
      }));
    } else {
      setNewTeacher(prev => ({
        ...prev,
        assignedSubjects: [...current, subject],
      }));
    }
  };

  const handleSelectAllLevel = (levelSubjects) => {
    const current = newTeacher.assignedSubjects || [];
    const allSelected = levelSubjects.every(s => current.includes(s));
    if (allSelected) {
      // Unselect this level
      setNewTeacher(prev => ({
        ...prev,
        assignedSubjects: current.filter(s => !levelSubjects.includes(s)),
      }));
    } else {
      // Select all in this level
      const combined = Array.from(new Set([...current, ...levelSubjects]));
      setNewTeacher(prev => ({
        ...prev,
        assignedSubjects: combined,
      }));
    }
  };

  const handleClearAllNewTeacherSubjects = () => {
    setNewTeacher(prev => ({
      ...prev,
      assignedSubjects: [],
    }));
  };

  const handleAddTeacher = async () => {
    if (!newTeacher.fullName.trim() || !newTeacher.email.trim() || !newTeacher.password.trim()) {
      alert('Please fill all required fields (Full Name, Email, Password)');
      return;
    }

    if (newTeacher.password.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }

    setIsAddingTeacher(true);
    try {
      await createUser({
        fullName: newTeacher.fullName.trim(),
        email: newTeacher.email.trim(),
        password: newTeacher.password,
        isActive: newTeacher.isActive,
        role: USER_ROLES.TEACHER,
        assignedSubjects: newTeacher.assignedSubjects || [],
      });
      setShowAddModal(false);
      setNewTeacher({
        fullName: '',
        email: '',
        password: '',
        isActive: true,
        assignedSubjects: [],
      });
      setShowNewTeacherPassword(false);
      alert('Teacher created and subjects assigned successfully!');
    } catch (error) {
      console.error('Error adding teacher:', error);
      const errorMessage = error.message || 'Failed to add teacher';
      alert(`Error adding teacher: ${errorMessage}`);
    } finally {
      setIsAddingTeacher(false);
    }
  };

  const handleSaveSubjects = async () => {
    if (!teacherToView) return;
    setIsSavingSubjects(true);
    try {
      await assignSubjectsToTeacher(teacherToView.id, editedSubjects);
      setTeacherToView(prev => ({ ...prev, assignedSubjects: editedSubjects }));
      setIsEditingSubjects(false);
      alert('Assigned subjects updated successfully');
    } catch (error) {
      console.error('Error saving subjects:', error);
      alert('Failed to update assigned subjects');
    } finally {
      setIsSavingSubjects(false);
    }
  };

  const handleToggleStatus = async (teacher) => {
    try {
      await toggleUserStatus(teacher.id, teacher.isActive);
    } catch (error) {
      alert('Failed to update teacher status');
    }
  };

  const handleDeleteTeacher = async () => {
    if (!teacherToDelete) return;

    setIsDeleting(true);
    try {
      await deleteUserAccount(teacherToDelete.id);
      setShowDeleteModal(false);
      setTeacherToDelete(null);
      alert('Success! Teacher account deleted successfully.');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete teacher';
      alert(`Error deleting teacher:\n\n${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Table columns
  const columns = [
    {
      header: 'Name',
      accessor: 'fullName',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 min-w-[2.5rem] rounded-full bg-gradient-to-br from-emerald-600 to-blue-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {value?.charAt(0) || 'T'}
          </div>
          <span className="font-medium text-gray-900">{value || 'N/A'}</span>
        </div>
      ),
    },
    {
      header: 'Email',
      accessor: 'email',
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      header: 'Assigned Subjects',
      accessor: 'assignedSubjects',
      render: (value) => (
        <span className="text-gray-700 font-medium">
          {value?.length || 0} {value?.length === 1 ? 'subject' : 'subjects'}
        </span>
      ),
    },
    {
      header: 'Uploaded Content',
      accessor: 'uploadedContent',
      render: (value) => (
        <span className="text-gray-600">
          {value?.lessons || 0} lessons, {value?.quizzes || 0} quizzes
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewTeacher(row)}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="View details"
          >
            <MdRemoveRedEye size={18} />
          </button>
          <button
            onClick={() => {
              setEditingTeacher(row);
              setShowEditModal(true);
            }}
            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            title="Edit teacher"
          >
            <MdEdit size={18} />
          </button>
          <button
            onClick={() => handleToggleStatus(row)}
            className={`p-2 rounded-lg transition-colors ${
              row.isActive 
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
            title={row.isActive ? 'Deactivate' : 'Activate'}
          >
            {row.isActive ? <MdToggleOff size={18} /> : <MdToggleOn size={18} />}
          </button>
          <button
            onClick={() => {
              setTeacherToDelete(row);
              setShowDeleteModal(true);
            }}
            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            title="Delete teacher"
          >
            <MdDelete size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">Teacher Management</h1>
            <p className="text-sm md:text-base text-gray-500">Manage teachers and their assigned subjects</p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-primary text-white rounded-lg md:rounded-xl font-semibold hover:bg-blue-700 transition-all text-sm md:text-base whitespace-nowrap flex-shrink-0">
            <MdAdd size={20} />
            <span className="hidden sm:inline">Add Teacher</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Subject Filter */}
        <select
          value={subjectFilter}
          onChange={(e) => setSubjectFilter(e.target.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
        >
          <option value="all">All Subjects</option>
          {allSubjects.map(subject => (
            <option key={subject} value={subject}>{formatSubject(subject)}</option>
          ))}
        </select>

        {/* Search Bar */}
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Total Teachers</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">{teachers.length}</p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Active Teachers</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">
            {teachers.filter(t => t.isActive).length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Subjects</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">{allSubjects.length}</p>
        </div>
      </div>

      {/* Loading/Empty States */}
      {loading ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">Loading teachers...</div>
      ) : filteredTeachers.length === 0 ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">No teachers found</div>
      ) : (
        <>
          {/* Mobile Card List - Hidden on Desktop */}
          <div className="block lg:hidden space-y-3">
            {filteredTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                {/* Teacher Header with Avatar and View Button */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="hidden sm:flex w-12 h-12 min-w-[3rem] rounded-full bg-gradient-to-br from-emerald-600 to-blue-500 items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {teacher.fullName?.charAt(0) || 'T'}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <h3 className="font-semibold text-gray-900 text-base truncate text-left">
                        {teacher.fullName || 'N/A'}
                      </h3>
                      <p className="text-sm text-gray-600 truncate text-left">{teacher.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleViewTeacher(teacher)}
                      className="p-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="View details"
                    >
                      <MdRemoveRedEye size={20} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTeacher(teacher);
                        setShowEditModal(true);
                      }}
                      className="p-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Edit teacher"
                    >
                      <MdEdit size={20} />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(teacher)}
                      className={`p-2.5 rounded-lg transition-colors ${
                        teacher.isActive 
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={teacher.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {teacher.isActive ? <MdToggleOff size={20} /> : <MdToggleOn size={20} />}
                    </button>
                    <button
                      onClick={() => {
                        setTeacherToDelete(teacher);
                        setShowDeleteModal(true);
                      }}
                      className="p-2.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete teacher"
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <DataTable columns={columns} data={filteredTeachers} />
            </div>
          </div>
        </>
      )}

        {/* Add Teacher Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setNewTeacher({ fullName: '', email: '', password: '', isActive: true, assignedSubjects: [] });
            setShowNewTeacherPassword(false);
          }}
          title="Add New Teacher"
          size="large"
        >
          <div className="space-y-4 md:space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTeacher.fullName}
                  onChange={(e) => setNewTeacher({ ...newTeacher, fullName: e.target.value })}
                  placeholder="e.g., Mr. John Doe"
                  className="w-full px-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                  placeholder="e.g., teacher@smartbrain.com"
                  className="w-full px-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewTeacherPassword ? 'text' : 'password'}
                  value={newTeacher.password}
                  onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  className="w-full pl-4 pr-11 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowNewTeacherPassword(!showNewTeacherPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                >
                  {showNewTeacherPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="activeTeacher"
                checked={newTeacher.isActive}
                onChange={(e) => setNewTeacher({ ...newTeacher, isActive: e.target.checked })}
                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary/20 cursor-pointer"
              />
              <label htmlFor="activeTeacher" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                Active (visible to students & staff)
              </label>
            </div>

            {/* Assigned Subjects Selector */}
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-bold text-darkNavy">
                    Assign Subjects <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                  </label>
                  {newTeacher.assignedSubjects?.length > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      {newTeacher.assignedSubjects.length} selected
                    </span>
                  )}
                </div>
                {newTeacher.assignedSubjects?.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllNewTeacherSubjects}
                    className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-200 max-h-72 overflow-y-auto space-y-4">
                {Object.entries(AVAILABLE_SUBJECTS).map(([level, subjects]) => {
                  const allLevelSelected = subjects.every(s => newTeacher.assignedSubjects?.includes(s));
                  return (
                    <div key={level} className="border-b border-gray-200/80 pb-3 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs md:text-sm font-bold text-darkNavy">{level} Classes</h4>
                        <button
                          type="button"
                          onClick={() => handleSelectAllLevel(subjects)}
                          className="text-xs text-primary hover:underline font-medium"
                        >
                          {allLevelSelected ? `Deselect All ${level}` : `Select All ${level}`}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 md:gap-2">
                        {subjects.map(subject => {
                          const isSelected = newTeacher.assignedSubjects?.includes(subject);
                          return (
                            <button
                              type="button"
                              key={subject}
                              onClick={() => toggleNewTeacherSubject(subject)}
                              className={`px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium border transition-all ${
                                isSelected 
                                  ? 'bg-primary text-white border-primary shadow-sm font-semibold' 
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary hover:bg-blue-50/50'
                              }`}
                            >
                              {formatSubjectShort(subject)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setNewTeacher({ fullName: '', email: '', password: '', isActive: true, assignedSubjects: [] });
                  setShowNewTeacherPassword(false);
                }}
                disabled={isAddingTeacher}
                className="px-5 md:px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm md:text-base disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTeacher}
                disabled={isAddingTeacher}
                className="px-6 md:px-8 py-2.5 md:py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-md text-sm md:text-base disabled:opacity-50"
              >
                {isAddingTeacher ? 'Adding Teacher...' : 'Add Teacher'}
              </button>
            </div>
          </div>
        </Modal>

      {/* View Teacher Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setTeacherToView(null);
        }}
        title="Teacher Details"
        size="medium"
      >
        {teacherToView && (
          <div className="space-y-4 md:space-y-6">
            {/* Teacher Avatar and Name */}
            <div className="flex items-center gap-3 md:gap-4 pb-4 md:pb-6 border-b border-gray-200">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-emerald-600 to-blue-500 flex items-center justify-center text-white font-bold text-xl md:text-2xl flex-shrink-0">
                {teacherToView.fullName?.charAt(0) || 'T'}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-bold text-darkNavy truncate">{teacherToView.fullName}</h3>
                <p className="text-sm md:text-base text-gray-500 truncate">{teacherToView.email}</p>
              </div>
            </div>

            {/* Teacher Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Status</label>
                <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm font-semibold ${
                  teacherToView.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {teacherToView.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-1">Joined Date</label>
                <p className="text-sm md:text-base text-gray-900 font-medium">
                  {teacherToView.createdAt ? (
                    teacherToView.createdAt.toDate ? 
                      teacherToView.createdAt.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) : new Date(teacherToView.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                  ) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Assigned Subjects */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs md:text-sm font-semibold text-gray-500">Assigned Subjects</label>
                {!isEditingSubjects && (
                  <button 
                    onClick={() => setIsEditingSubjects(true)} 
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs md:text-sm font-semibold hover:bg-blue-100 transition-colors"
                  >
                    <MdEdit size={16} /> Edit
                  </button>
                )}
              </div>
              
              {!isEditingSubjects ? (
                <div className="flex flex-wrap gap-2">
                  {teacherToView.assignedSubjects && teacherToView.assignedSubjects.length > 0 ? (
                    teacherToView.assignedSubjects.map((subject, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs md:text-sm font-medium"
                      >
                        {formatSubject(subject)}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-xs md:text-sm">No subjects assigned</p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {Object.entries(AVAILABLE_SUBJECTS).map(([level, subjects]) => (
                    <div key={level} className="mb-4 last:mb-0">
                      <h4 className="text-sm font-bold text-darkNavy mb-2">{level} Classes</h4>
                      <div className="flex flex-wrap gap-2">
                        {subjects.map(subject => {
                          const isSelected = editedSubjects.includes(subject);
                          return (
                            <button
                              key={subject}
                              onClick={() => {
                                if (isSelected) {
                                  setEditedSubjects(editedSubjects.filter(s => s !== subject));
                                } else {
                                  setEditedSubjects([...editedSubjects, subject]);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium border transition-colors ${
                                isSelected 
                                  ? 'bg-primary text-white border-primary' 
                                  : 'bg-white text-gray-600 border-gray-300 hover:border-primary'
                              }`}
                            >
                              {formatSubjectShort(subject)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Uploaded Content Stats */}
            <div className="bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4">
              <label className="block text-xs md:text-sm font-semibold text-gray-500 mb-3">Uploaded Content</label>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Lessons</p>
                  <p className="text-lg md:text-xl font-bold text-darkNavy">
                    {teacherToView.uploadedContent?.lessons || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600">Quizzes</p>
                  <p className="text-lg md:text-xl font-bold text-darkNavy">
                    {teacherToView.uploadedContent?.quizzes || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
              {isEditingSubjects && (
                <>
                  <button
                    type="button"
                    disabled={isSavingSubjects}
                    onClick={() => {
                      setIsEditingSubjects(false);
                      setEditedSubjects(teacherToView.assignedSubjects || []);
                    }}
                    className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg md:rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm md:text-base disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSavingSubjects}
                    onClick={handleSaveSubjects}
                    className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg md:rounded-xl font-bold hover:bg-blue-700 transition-all shadow-md text-sm md:text-base transform hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {isSavingSubjects ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
              {!isEditingSubjects && (
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setTeacherToView(null);
                  }}
                  className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg md:rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm md:text-base"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Teacher Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingTeacher(null);
        }}
        title="Edit Teacher"
        size="medium"
      >
        {editingTeacher && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={editingTeacher.fullName || ''}
                onChange={(e) => setEditingTeacher({ ...editingTeacher, fullName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={editingTeacher.email || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation.</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="editActiveTeacher"
                checked={editingTeacher.isActive !== false}
                onChange={(e) => setEditingTeacher({ ...editingTeacher, isActive: e.target.checked })}
                className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary/20"
              />
              <label htmlFor="editActiveTeacher" className="text-sm font-medium text-gray-700">Active (visible to users)</label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTeacher(null);
                }}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >Cancel</button>
              <button
                onClick={async () => {
                  if (!editingTeacher.fullName) {
                    alert('Full name is required.');
                    return;
                  }
                  try {
                    await updateUser(editingTeacher.id, {
                      fullName: editingTeacher.fullName,
                      isActive: editingTeacher.isActive,
                    });
                    setShowEditModal(false);
                    setEditingTeacher(null);
                    alert('Teacher updated successfully');
                  } catch (error) {
                    console.error('Error updating teacher:', error);
                    alert('Failed to update teacher');
                  }
                }}
                className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >Save Changes</button>
            </div>
          </div>
        )}
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTeacherToDelete(null);
        }}
        title="Delete Teacher"
        size="small"
      >
        <div>
          <p className="mb-3 text-gray-700">
            Are you sure you want to delete{' '}
            <strong className="text-darkNavy">{teacherToDelete?.fullName}</strong>?
          </p>
          <p className="text-sm text-red-600 mb-6">This action cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setTeacherToDelete(null);
              }}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleDeleteTeacher}
              disabled={isDeleting}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-all hover:shadow-lg ${
                isDeleting ? 'bg-red-400 cursor-not-allowed text-white' : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isDeleting ? 'Deleting...' : 'Delete Teacher'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Teachers;
